import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { ErrorHandler, inject, ModuleWithProviders, NgModule, PLATFORM_ID, provideAppInitializer } from '@angular/core';
import {
  AuthConfig,
  OAuthModule,
  OAuthModuleConfig,
  OAuthStorage
} from 'angular-oauth2-oidc';
import { AuthGuard } from '@core/services/auth/auth-guard.service';
import { AuthorityGuard } from '@core/services/auth/authority-guard.service';
import { authModuleConfig } from '@core/services/auth/auth-module-config';
import { AuthService } from '@core/services/auth/auth.service';
import { isPlatformBrowser } from '@angular/common';
import { LocalStorageService } from '@core/services/local-storage/local-storage.service';
import { AppErrorHandler } from '@core/services/error-handler/error-handler.service';
import { HttpCacheService } from '@core/services/http/http-cache.service';
import { UserService } from '@core/data/service/user.service';
import { HttpCacheInterceptor } from '@core/services/http/http-cache-interceptor.service';
import { HttpErrorInterceptor } from '@core/services/http/http-error-interceptor.service';
import { NGXLogger } from 'ngx-logger';
import { HttpBaseUrlInterceptor } from '@core/services/http/http-base-url-interceptor.service';
import { HttpFieldExcluderInterceptor } from '@core/services/http/http-field-excluder-interceptor.service';
import { HttpDateConverterInterceptor } from '@core/services/http/http-date-converter-interceptor.service';
import { TitleService } from '@core/services/title.service';
import { ClarityService } from '@core/services/clarity.service';
import { authConfig } from '@core/services/auth/auth-config';
import { HttpClassTransformInterceptor } from '@core/services/http/http-class-transform-interceptor';
import { BillingService } from '@core/data/service/billing.service';
import { firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { delay } from '@core/utils';
import { NotificationService } from '@core/data/service/notification.service';
import { LANDING_PATH } from '@core/services/auth/landing-path';
import { WorkflowService } from '@core/data/service/workflow.service';
import { RunService } from '@core/data/service/run.service';
import { InboxService } from '@core/data/service/inbox.service';
import { ConnectionService } from '@core/data/service/connection.service';
import { DirectoryService } from '@core/data/service/directory.service';

// We need a factory since localStorage is not available at AOT build time
export function storageFactory(platformId: Object): OAuthStorage | null {
  if (isPlatformBrowser(platformId)) {
    return localStorage;
  }
  return null;
}

const MAX_SUBSCRIPTION_STATUS_CHECK_ATTEMPT = 3;
const SUBSCRIPTION_STATUS_CHECK_INTERVAL = 1500;

type SubscriptionRedirectResult = 'plans' | 'none';

async function handleSubscription(billingService: BillingService, router: Router, attempt: number): Promise<SubscriptionRedirectResult> {
  let subscription;

  try {
    subscription = await firstValueFrom(billingService.findSubscription());
  } catch (ex) {
    subscription = undefined;
  }

  // Landing page after Stripe checkout. It renders nothing but "Loading..." until the subscription
  // turns ACTIVE, so wait for the webhook rather than leaving the user on a dead page.
  if (window.location.pathname === '/settings/billing' && !subscription?.isActive) {
    if (attempt < MAX_SUBSCRIPTION_STATUS_CHECK_ATTEMPT) {
      await delay(SUBSCRIPTION_STATUS_CHECK_INTERVAL);
      return await handleSubscription(billingService, router, attempt + 1);
    }
    if (subscription?.hasAppAccess) {
      await router.navigate(['/settings/billing-usage'], { replaceUrl: true });
      return 'none';
    }
  }

  if (!subscription?.hasAppAccess) {
    if (!window.location.href.includes('/plans')) {
      await router.navigate(['plans'], { replaceUrl: true });
    }
    return 'plans';
  }

  // Active subscribers reach /plans from Billing & usage to upgrade or downgrade; trials use it to convert.
  return 'none';
}

const coreServices = [
  AuthService,
  LocalStorageService,
  HttpCacheService,
  TitleService
];

const dataServices = [
  UserService,
  BillingService,
  NotificationService,
  WorkflowService,
  RunService,
  InboxService,
  ConnectionService,
  DirectoryService
];

const httpInterceptors = [
  { provide: HTTP_INTERCEPTORS, useClass: HttpBaseUrlInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: HttpFieldExcluderInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: HttpDateConverterInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: HttpClassTransformInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true, deps: [AppErrorHandler, AuthService] },
  { provide: HTTP_INTERCEPTORS, useClass: HttpCacheInterceptor, multi: true, deps: [HttpCacheService, NGXLogger] },
];

@NgModule({
  imports: [OAuthModule.forRoot()], providers: [
    ...coreServices,
    ...dataServices,
    { provide: ErrorHandler, useClass: AppErrorHandler },
    ...httpInterceptors,
    AuthGuard,
    AuthorityGuard,
    provideHttpClient(withFetch()),
    provideHttpClient(withXhr(), withInterceptorsFromDi())
  ]
})
export class CoreModule {
  static forRoot(): ModuleWithProviders<CoreModule> {
    return {
      ngModule: CoreModule,
      providers: [
        provideAppInitializer(() => {
          const authService = inject(AuthService);
          const billingService = inject(BillingService);
          const userService = inject(UserService);
          const router = inject(Router);
          const clarityService = inject(ClarityService);

          clarityService.start();
          return authService.runInitialLoginSequence().then(async () => {
            if (authService.hasValidToken()) {
              clarityService.tagUser(userService.currentUser);
              const result = await handleSubscription(billingService, router, 0);
              // The '' route only shows a spinner: send a signed-in subscriber to their landing page.
              if (result !== 'plans' && ['/', ''].includes(window.location.pathname)) {
                await router.navigate([LANDING_PATH], { replaceUrl: true });
              }
            }
          });
        }),
        { provide: AuthConfig, useValue: authConfig },
        { provide: OAuthModuleConfig, useValue: authModuleConfig },
        { provide: OAuthStorage, useFactory: storageFactory, deps: [PLATFORM_ID] }
      ]
    };
  }

  constructor() {
    const parentModule = inject(CoreModule, { optional: true, skipSelf: true });

    if (parentModule) {
      throw new Error('CoreModule is already loaded. Import it in the AppModule only');
    }
  }
}
